import {
  getTrackingTokenFromLocalStorage,
  isInAppBrowser,
} from 'helpers/browser';
import { KustomerMain } from 'core/types';
import { getStore, updateStore } from 'core_main/store';
import { fetchSchedules } from 'core_main/api/schedules';
import { fetchCurrentTrackingToken } from 'core_main/api/tracking';
import { fetchSettings } from 'core_main/api/settings';
// eslint-disable-next-line import/no-cycle
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { handleError, ErrorMessage, debug, LogLabel } from 'globals/errors';
import { initSentry } from 'globals/sentry';
import { syncHistoryFilterStore } from 'core_main/sdk/helpers';
import { createInitCallbackResponse } from './helpers';
// Health check monitoring is lazy-loaded to reduce initial bundle size
// It's only needed when PubNub is initialized

/*
  This initializes the Core SDK by fetching the chat configuration and initializing pubnub
  Pubnub will only be initialized if the following is true (flowchart https://miro.com/app/board/o9J_kuhgNHQ=/) :
    - The tracking token exists in local storage
    - The tracking token is connected to a customerId

*/
export const init: KustomerMain['init'] = (parameters?, cb?) => {
  debug({ message: 'SDK init() called', labels: [LogLabel.PUBNUB_RECOVERY] });
  const originalTrackingToken = getTrackingTokenFromLocalStorage();
  debug({
    message: `Tracking token ${originalTrackingToken ? 'found' : 'not found'}`,
    labels: [LogLabel.PUBNUB_RECOVERY],
  });

  // Always set testAutomationId (either to provided value or undefined)
  // This ensures clean state between init calls
  updateStore('testAutomationId', parameters?.testAutomationId);
  updateStore(
    'useLegacyConversationPreview',
    parameters?.useLegacyConversationPreview ?? false,
  );

  // Set enableUrlUnfurling flag (defaults to true; consumers may pass false to opt out)
  updateStore('enableUrlUnfurling', parameters?.enableUrlUnfurling ?? true);

  syncHistoryFilterStore(updateStore, parameters);

  return Promise.all([
    fetchSettings({
      brandId: parameters?.brandId,
      lang: parameters?.lang,
      assistantId: parameters?.assistantId,
    }),
    fetchSchedules(parameters?.scheduleId),
  ])
    .then((response) => {
      const sentryKey = response[0]?.data?.attributes?.sentry?.web;
      if (sentryKey && !isInAppBrowser()) {
        initSentry(sentryKey, getStore().orgId);
      }

      const { enabled } = getStore().settings;
      if (!enabled) {
        const message = ErrorMessage.CHAT_DISABLED;

        console.warn(message);

        return Promise.reject(new Error(message));
      }

      if (originalTrackingToken === null) {
        debug({
          message: 'No tracking token, returning early',
          labels: [LogLabel.PUBNUB_RECOVERY],
        });
        updateStore('isPubNubConnected', false);
        return undefined;
      }

      debug({
        message: 'Fetching current tracking token',
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
      return fetchCurrentTrackingToken().then((response) => {
        debug({
          message: `Tracking token response received: ${
            response ? 'success' : 'failed'
          }`,
          labels: [LogLabel.PUBNUB_RECOVERY],
        });
        const customerId = response?.data?.relationships?.customer?.data?.id;
        debug({
          message: `Customer ID extracted: ${
            customerId ? 'success' : 'undefined'
          }`,
          labels: [LogLabel.PUBNUB_RECOVERY],
        });

        updateStore('customerId', customerId);

        if (customerId === undefined) {
          debug({
            message: 'No customerId, returning early',
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
          updateStore('isPubNubConnected', false);
          return undefined;
        }

        // Trust the tracking-token API customer id (survivor after merge once re-keyed).
        // Clear the connected flag so importAndInitializePubNub does not short-circuit on
        // re-init (reloadSettings) or when the id changed since the last connection.
        updateStore('isPubNubConnected', false);

        const { orgId } = getStore();

        // Set up visibility and network monitoring for PubNub health checks
        // Lazy-load the monitoring setup to reduce initial bundle size
        import(
          /* webpackChunkName: "pubnub-health-check" */ 'core_main/pubnub/helpers/checkAndReconnect'
        )
          .then((module) => {
            module.setupHealthCheckMonitoring();
          })
          .catch((error) => {
            // If monitoring setup fails, log but don't block initialization
            debug({
              message: 'Failed to set up health check monitoring',
              labels: [LogLabel.PUBNUB_RECOVERY],
            });
            handleError(error, {
              message: 'Failed to set up health check monitoring',
              labels: [LogLabel.PUBNUB_RECOVERY],
            });
          });

        const callbackResponse = createInitCallbackResponse();
        debug({
          message: `About to call importAndInitializePubNub, hasCustomerId: ${Boolean(
            customerId,
          )}, orgId: ${orgId}`,
          labels: [LogLabel.PUBNUB_RECOVERY],
        });
        return importAndInitializePubNub({
          customerId,
          orgId,
          cb: () => {
            if (typeof cb === 'function') cb(callbackResponse);
          },
        }).then(() => {
          debug({
            message: 'importAndInitializePubNub completed',
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
          return callbackResponse;
        });
      });
    })
    .then((callbackResponse) => {
      // if we already built the callbackResponse it means we passed the callback to pubnub, so don't call it here
      if (callbackResponse) return;
      if (typeof cb === 'function') {
        const callbackResponse = createInitCallbackResponse();
        cb(callbackResponse);
      }
    })
    .catch((error) => {
      // Clean up listeners and timers if they were set up
      // Lazy-load cleanup to avoid pulling in health check code if initialization fails early
      import(
        /* webpackChunkName: "pubnub-health-check" */ 'core_main/pubnub/helpers/checkAndReconnect'
      )
        .then((module) => {
          module.cleanupHealthCheckMonitoring();
        })
        .catch(() => {
          // Ignore cleanup errors - if module isn't loaded yet, there's nothing to clean up
        });

      if (typeof cb === 'function') {
        cb(null, error);
      }
      handleError(error, {
        message: 'Failed to initialize',
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
    });
};
